import { DestroyRef, Injectable, computed, signal, inject } from '@angular/core';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY, Observable, concat, filter, ignoreElements, map, switchMap, take, throwError, catchError, tap, finalize, shareReplay, of } from 'rxjs';
import { 
  BusinessProfile, 
  AuthState, 
  LoginRequest, 
  RegisterRequest, 
  TokenResponse, 
  CurrentUser,
  RefreshTokenRequest,
  LogoutRequest,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetConfirmRequest,
  BusinessDataRequest,
  RegistrationData,
  ORGANIZATION_TYPE_TO_API_MAP
} from '../types/auth.types';
import { ApiService } from './api.service';
import { DiagnosticsService } from './diagnostics.service';
import { TokenRefreshLock } from './token-refresh-lock.service';
import { ConfirmationService } from './confirmation.service';
import { UNSAVED_CHANGES_CONFIRMATION, UnsavedChangesService } from './unsaved-changes.service';
import {
  businessScopeIds,
  hasBusinessPermission,
  hasCabinetPermission,
  hasPermission,
} from '../utils/auth-permissions';
import {
  currentAuthRole,
  currentUserIdOf,
  isAccountantRole,
  isInternalRole,
  isAccountingContourRole,
  isAdminContourRole,
  isAdminRole,
  isBusinessContourRole,
  isBusinessRole,
  isBusinessUserRole,
  isManagerRole
} from '../utils/auth-roles';
import { isPublicPath, pathOnly } from '../utils/auth-routing';
import { withoutOfflineOverlay } from '../interceptors/network-resilience.interceptor';

/** Рефрешить нечем: refresh-токена в хранилище нет — сессии уже нет. */
class NoRefreshTokenError extends Error {
  constructor() {
    super('No refresh token available');
  }
}

/** BE-1490: вкладка перезагружается под пользователем, вошедшим в соседней, — не конец сессии. */
class ReloadingForOtherUserError extends Error {
  constructor() {
    // BE-1518: текст видит человек — экраны показывают его в тосте ошибки.
    super('Вкладка перезагружается под аккаунтом, вошедшим в соседней вкладке');
  }
}

/**
 * BE-1518: запрос вкладки не ушёл — пара в общем хранилище не её: в соседней вкладке вошли под другим
 * аккаунтом (или ещё решается конец сессии). Не HttpErrorResponse: это не обрыв связи и не ответ
 * сервера — без «Нет связи», повторов и конца сессии.
 */
export class SessionPairBlockedError extends Error {
  constructor(message = 'В соседней вкладке вошли под другим аккаунтом — обновите страницу') {
    super(message);
  }
}

/** BE-1490: куда вкладке после выхода — на вход или перезагрузка под вошедшим в соседней. */
type AfterLogout = 'login' | 'reload';

/**
 * BE-1491: `info` перехода на вход, при котором гвард несохранённых изменений не спрашивает:
 * человек уже ответил «Уйти» на «Выход» или сам нажал «Войти» на плашке «Сессия закончилась».
 * Метка едет с самим переходом, а не флагом в сервисе, — не переживёт отменённый переход.
 */
export const LEAVE_WITHOUT_QUESTION = 'leave-without-unsaved-question';

/**
 * BE-1478: бэк отказал в обновлении (4xx: refresh ротирован, отозван, протух или не того
 * формата). Обрыв связи, 5xx и таймаут сюда НЕ входят — это не конец сессии.
 */
function isRefreshRejected(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500;
}

/** Чей это access-токен: `user_id` из JWT (подпись не проверяем — только сравнить вкладки). */
function tokenUserId(token: string | null | undefined): string | null {
  try {
    const payload = token?.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))?.user_id ?? null;
  } catch {
    return null;
  }
}

/**
 * Почта для входа и сброса пароля: `lower` — в нижнем регистре, `typed` — как её ввели, если
 * отличается. Бэк хранит почту в нижнем регистре и ищет строгим сравнением — «Ivan@mail.ru» не
 * находит «ivan@mail.ru», поэтому первой идёт `lower`. Аккаунты, заведённые до 04.02.2026,
 * хранятся как их ввели, в том числе с заглавными, — им остаётся `typed`. Домен бэк сам опускает
 * в нижний регистр, поэтому и в `typed` он опущен: заглавные только в домене второго запроса не
 * дают (иначе на сброс пароля ушло бы два одинаковых письма).
 */
function loginEmails(raw: string): { lower: string; typed: string | null } {
  const trimmed = (raw ?? '').trim();
  const at = trimmed.lastIndexOf('@');
  const typed =
    at < 0 ? trimmed : trimmed.slice(0, at) + trimmed.slice(at).toLowerCase();
  const lower = trimmed.toLowerCase();
  return { lower, typed: typed === lower ? null : typed };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly AUTH_KEY = 'zhmyak-auth';
  private readonly PROFILE_KEY = 'zhmyak-business-profile';

  /**
   * Single-flight рефреша: бэкенд РОТИРУЕТ refresh-токены (saveTokens перезаписывает
   * refresh_token), поэтому два параллельных POST /auth/refresh со старым токеном →
   * второй летит с уже инвалидированным токеном → спонтанный разлогин. Держим один
   * общий in-flight Observable: все параллельные 401 ждут ОДИН рефреш и повторяются
   * с ОДНИМ новым токеном. Сбрасывается в finalize по завершении (успех/ошибка).
   */
  private refreshInFlight$: Observable<TokenResponse> | null = null;

  /**
   * Идемпотентный страж выхода на провале рефреша. Тимдаун (expireSession: локальная
   * очистка + /auth, если страница закрытая) живёт на ОБЩЕМ пайпе refreshInFlight$ (а не в catchError каждого
   * ждущего запроса), поэтому при пачке из N параллельных 401, чей рефреш упал, выход
   * выполняется РОВНО ОДИН раз — без гонки N навигаций и дублей хлебных крошек.
   * Снимается, когда сессия снова есть: saveTokens или принятие пары другой вкладки.
   */
  private isHandlingAuthFailure = false;

  /** BE-804: один перезапрос прав в полёте — пачка 403 не должна множить /auth/me. */
  private permissionsRefreshInFlight = false;

  /** BE-1489: «Выход» уже отправлен — повторный клик, пока ждём ответа, второй POST не шлёт. */
  private logoutInFlight = false;

  /**
   * BE-1491: сессия кончилась на странице с несохранёнными изменениями, и на вопрос ответили
   * «Остаться» — на странице плашка «Сессия закончилась» (app-session-ended-banner). Пока она
   * видна, следующие 401 сессию заново не «кончают» и вопрос не задают. Сбрасывается при входе
   * и при уходе со страницы.
   */
  private readonly sessionEndedOnFormState = signal(false);
  readonly sessionEndedOnForm = this.sessionEndedOnFormState.asReadonly();

  /** BE-1491: конец сессии уже уводит на вход (вопрос «Уйти / Остаться» может быть открыт). */
  private sessionEnding = false;

  /**
   * BE-1490: вкладка уже перезагружается под пользователем, вошедшим в соседней. До
   * перезагрузки она его пару не обменивает и не отзывает, а сама второй раз не
   * перезагружается — разве что человек снова нажмёт «Выход» (перезагрузку могли отменить).
   * BE-1518: перезагрузку отменили (несохранённые данные) или вкладка осталась без своей сессии —
   * её запросы с его парой не уходят; на странице плашка «вошли под другим аккаунтом».
   */
  private readonly otherUserSignedInState = signal(false);
  readonly otherUserSignedIn = this.otherUserSignedInState.asReadonly();

  /**
   * BE-1518: состояние вкладки на момент конца сессии. По нему решаем, тот ли пользователь вошёл в
   * соседней вкладке, и из него же возвращаем роль и права, если вкладка оживёт.
   */
  private endedSession: AuthState | null = null;

  public authState = signal<AuthState>({
    user: null,
    currentUser: null,
    businessProfile: null,
    isAuthenticated: false,
    isLoading: false,
    tokens: null
  });

  /**
   * BE-1157: роль текущего пользователя — здесь, а не в каждом гварде и экране.
   * Источник ровно один: роль из /auth/me, а пока ответа нет — роль из токена
   * (гварды отрабатывают раньше загрузки профиля). Что означает каждая роль,
   * по-прежнему описано в `core/utils/auth-roles`.
   */
  readonly role = computed(() => currentAuthRole(this.authState()));

  readonly isAdmin = computed(() => isAdminRole(this.role()));
  readonly isManager = computed(() => isManagerRole(this.role()));
  /** Админ-контур: админ, root или менеджер — те, кому открыта админка. */
  readonly isAdminContour = computed(() => isAdminContourRole(this.role()));
  readonly isAccountant = computed(() => isAccountantRole(this.role()));
  /** BE-824: любой сотрудник платформы — админ, root, менеджер, бухгалтер. */
  readonly isInternal = computed(() => isInternalRole(this.role()));
  /** Бухгалтерский контур шире роли бухгалтера: раздел открыт и админу. */
  readonly isAccountingContour = computed(() => isAccountingContourRole(this.role()));
  readonly isBusiness = computed(() => isBusinessRole(this.role()));
  readonly isBusinessUser = computed(() => isBusinessUserRole(this.role()));
  /** Бизнес-контур: владелец бизнеса или его сотрудник. */
  readonly isBusinessContour = computed(() => isBusinessContourRole(this.role()));

  /** id текущего пользователя (см. `currentUserIdOf`): «это я» в списках, себя — в assign_manager. */
  readonly currentUserId = computed(() => currentUserIdOf(this.authState().currentUser));

  /**
   * BE-804: гранулярные права из /auth/me — здесь, а не в каждом экране. Что означает
   * каждое правило (админ в обход, незагруженный профиль, пустой скоуп), описано
   * в `core/utils/auth-permissions`.
   */
  readonly permissions = computed(() => this.authState().currentUser?.permissions);

  /** Бизнесы в скоупе пользователя: у менеджера — закреплённые за ним. */
  readonly scopedBusinessIds = computed(() => businessScopeIds(this.authState()));

  /** Есть ли право вообще (для разделов меню и гвардов). */
  can(key: string): boolean {
    return hasPermission(this.authState(), key);
  }

  /** Есть ли право по конкретному бизнесу (для действий в его карточке). */
  canForBusiness(key: string, businessId: string | null | undefined): boolean {
    return hasBusinessPermission(this.authState(), key, businessId ?? null);
  }

  /**
   * BE-807: есть ли право в бизнес-кабинете. Отдельно от `canForBusiness`, потому что
   * пустой скоуп здесь значит «прав нет», а не «прав не знаем» — см. `hasCabinetPermission`.
   */
  canInCabinet(key: string, businessId: string | null | undefined): boolean {
    return hasCabinetPermission(this.authState(), key, businessId ?? null);
  }

  /**
   * Перечитать профиль вместе с правами.
   *
   * Нужен в двух случаях: старт приложения с уже сохранённой сессией (профиль лежит в
   * localStorage с момента входа, а права админ мог поменять) и 403
   * `auth.permission_denied` — признак того, что интерфейс разошёлся с бэкендом.
   * Ошибку глушим: 401 разбирает интерцептор, а из-за сети или 5xx терять сессию нельзя.
   */
  refreshPermissions(): void {
    // Экран роняет в 403 сразу пачку запросов (список + счётчики): без этого стража
    // каждый из них поднимал бы свой /auth/me.
    if (this.permissionsRefreshInFlight) {
      return;
    }
    this.permissionsRefreshInFlight = true;
    this.getCurrentUser()
      .pipe(finalize(() => (this.permissionsRefreshInFlight = false)))
      .subscribe({ error: () => undefined });
  }

  /**
   * Перечитать профиль на старте приложения, если сессия уже есть.
   *
   * Профиль (а с ним права) сохраняется при входе и дальше живёт в localStorage: без
   * этого выданное или снятое админом право не подхватится до следующего логина, и меню
   * расходилось бы с тем, что разрешает бэкенд. Вызывается из `provideAppInitializer` —
   * запрос на старте приложения, а не в конструкторе сервиса, который поднимается
   * в том числе в спеках и по цепочке инъекций.
   */
  refreshPermissionsIfSignedIn(): void {
    if (localStorage.getItem('access_token') && localStorage.getItem('refresh_token')) {
      this.refreshPermissions();
    }
  }

  private router = inject(Router);
  private readonly location = inject(Location);
  private readonly refreshLock = inject(TokenRefreshLock);
  private readonly confirmation = inject(ConfirmationService);
  private readonly unsavedChanges = inject(UnsavedChangesService);

  constructor(
    private apiService: ApiService,
    private diagnostics: DiagnosticsService
  ) {
    this.initializeAuth();
    // BE-1478: другая вкладка обновила пару — подхватываем её в сигнал этой вкладки.
    const onStorage = (event: StorageEvent) => this.syncTokensFromOtherTab(event);
    globalThis.addEventListener?.('storage', onStorage);
    inject(DestroyRef).onDestroy(() => globalThis.removeEventListener?.('storage', onStorage));
  }

  /** Перезагрузка вкладки; полем — чтобы спеки могли её подменить. */
  protected reloadPage = (): void => globalThis.location.reload();

  // API Auth Methods
  login(credentials: LoginRequest): Observable<TokenResponse> {
    this.authState.update(state => ({ ...state, isLoading: true }));

    const { lower, typed } = loginEmails(credentials.email);
    return this.apiService.login({ ...credentials, email: lower }).pipe(
      // Не подошло в нижнем регистре — возможно, старый аккаунт с заглавными в почте.
      catchError((error) =>
        error?.status === 401 && typed
          ? this.apiService.login({ ...credentials, email: typed })
          : throwError(() => error)
      ),
      tap((tokens) => {
        this.saveTokens(tokens);
        this.authState.update(state => ({
          ...state,
          tokens,
          isAuthenticated: true,
          isLoading: false
        }));
        this.saveAuthState();
      }),
      catchError((error) => {
        this.authState.update(state => ({ ...state, isLoading: false }));
        return throwError(() => error);
      })
    );
  }

  register(userData: RegisterRequest): Observable<TokenResponse> {
    this.authState.update(state => ({ ...state, isLoading: true }));

    return this.apiService.register(userData).pipe(
      tap((tokens) => {
        this.saveTokens(tokens);
        this.authState.update(state => ({
          ...state,
          tokens,
          isAuthenticated: true,
          isLoading: false
        }));
        this.saveAuthState();
      }),
      catchError((error) => {
        this.authState.update(state => ({ ...state, isLoading: false }));
        return throwError(() => error);
      })
    );
  }

  /**
   * Обновить access-токен. `failedAccessToken` — токен, с которым запрос получил 401:
   * если в хранилище уже другой, пару обновила соседняя вкладка, и в сеть идти не нужно.
   */
  refreshToken(failedAccessToken?: string | null): Observable<TokenResponse> {
    // Если рефреш уже в полёте — все параллельные 401 ждут ОДИН результат и
    // повторяются с ОДНИМ новым токеном (см. refreshInFlight$).
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    // BE-1478: внутри вкладки — single-flight, между вкладками — общая очередь
    // (TokenRefreshLock): refresh ротируется, и два одновременных обмена одним и тем же
    // токеном выбили бы одну из вкладок.
    this.refreshInFlight$ = this.refreshLock.run(() => this.refreshUnderLock(failedAccessToken)).pipe(
      tap({
        // Единый тимдаун на ОБЩЕМ пайпе — ровно один раз на всю пачку ждущих 401, и только
        // когда сессии действительно нет: бэк отказал (4xx) или refresh-токена нет вовсе
        // (иначе приложение залипло бы в «авторизован, но обновить нечем»). Обрыв связи,
        // 5xx и таймаут получает лишь этот запрос — следующий попробует обновить заново.
        error: (error) => {
          if (error instanceof NoRefreshTokenError || isRefreshRejected(error)) {
            this.handleRefreshFailure();
          }
        }
      }),
      // finalize ДО shareReplay → сброс in-flight привязан к завершению ОБЩЕГО
      // источника (рефреш завершён/упал), а НЕ к отписке каждого ждущего. Если бы
      // finalize стоял ПОСЛЕ shareReplay, он бы срабатывал на КАЖДУЮ отписку: ранний
      // unsubscribe одного waiter'а (смена роута / отменённый HTTP-подписчик) обнулил
      // бы lock на лету, и следующий 401 поднял бы ВТОРОЙ POST /auth/refresh тем же
      // ротируемым токеном — ровно та гонка разлогина, которую single-flight чинит.
      finalize(() => {
        // Сбрасываем ТОЛЬКО in-flight (разрешаем следующий рефреш). Страж
        // isHandlingAuthFailure здесь НЕ трогаем: синхронный no-token throwError
        // сбрасывал бы его мгновенно, и пачка «нет refresh-токена» 401 тимдаунила
        // бы по разу на КАЖДЫЙ запрос. Страж снимается при следующей удачной
        // авторизации (saveTokens) — когда сессия реально восстановлена.
        this.refreshInFlight$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshInFlight$;
  }

  /**
   * Сам обмен — уже в очереди. Пару перечитываем из хранилища ЗДЕСЬ, а не до очереди:
   * пока ждали, её могла обновить другая вкладка. Новую пару сохраняем тоже здесь, до
   * освобождения очереди, — следующая вкладка должна увидеть её готовой.
   */
  private refreshUnderLock(failedAccessToken?: string | null): Observable<TokenResponse> {
    // BE-1490: вкладка перезагружается под пользователем, вошедшим в соседней (обновление
    // ждало очереди за «Выходом»), — его пару не обмениваем и его профиль не переписываем.
    if (this.otherUserSignedInState()) {
      return throwError(() => new SessionPairBlockedError());
    }
    const storedAccess = localStorage.getItem('access_token');
    const storedRefresh = localStorage.getItem('refresh_token');
    if (!storedRefresh) {
      return throwError(() => new NoRefreshTokenError());
    }
    // BE-1518: своей сессии у вкладки нет (плашка, открыт вопрос) — пара в хранилище может быть
    // чужой: чья она, решает сверка пары, а не обновление.
    if (!this.authState().tokens) {
      return throwError(() => new SessionPairBlockedError('Сессия закончилась — войдите снова'));
    }
    if (failedAccessToken && storedAccess && storedAccess !== failedAccessToken) {
      return this.adoptStoredPair$();
    }

    return this.apiService.refreshToken({ refresh_token: storedRefresh }).pipe(
      switchMap((tokens) => {
        // BE-1490: пока шёл обмен, пару в хранилище сменили мимо очереди — вход или выход по
        // 401 в соседней вкладке, а без navigator.locks и её «Выход» или обмен. Выданную пару
        // не сохраняем (иначе сессия «оживёт» после «Выхода») и отзываем.
        const currentRefresh = localStorage.getItem('refresh_token');
        if (currentRefresh !== storedRefresh) {
          this.revokeQuietly(tokens);
          if (!currentRefresh) return throwError(() => new NoRefreshTokenError());
          // Там уже пара другого пользователя — вкладку перезагружаем под ним, его пару и
          // профиль не трогаем; пару того же пользователя принимаем, как обмен соседней вкладки.
          if (this.isOtherUser(tokens.access_token, localStorage.getItem('access_token'))) {
            this.reloadForOtherUser();
            return throwError(() => new ReloadingForOtherUserError());
          }
          return of(this.adoptStoredTokens());
        }
        this.saveTokens(tokens);
        this.authState.update(state => ({
          ...state,
          tokens,
          isAuthenticated: true
        }));
        this.saveAuthState();
        return of(tokens);
      }),
      catchError((error) => {
        // Отказ мог прийти потому, что этот refresh только что ротировала другая вкладка
        // (без общей очереди: нет navigator.locks или вышло ожидание). Тогда её пара и
        // есть наша — выходить не за что.
        const currentRefresh = localStorage.getItem('refresh_token');
        if (isRefreshRejected(error) && currentRefresh && currentRefresh !== storedRefresh) {
          return this.adoptStoredPair$();
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * BE-1518: пару, которую положила соседняя вкладка, берём, только если она того же пользователя.
   * Вошёл другой — вкладка перезагружается под ним, а его пару и профиль не трогаем.
   */
  private adoptStoredPair$(): Observable<TokenResponse> {
    if (this.storedPairOfOtherUser()) {
      this.reloadForOtherUser();
      return throwError(() => new ReloadingForOtherUserError());
    }
    return of(this.adoptStoredTokens());
  }

  /**
   * Пара в хранилище свежее, чем в сигнале: её положила другая вкладка. `persist` —
   * переписать ли общий профиль (zhmyak-auth): вкладке, которая лишь узнала о чужом
   * обновлении, писать его не нужно — она затёрла бы более свежий профиль источника.
   */
  private adoptStoredTokens(persist = true): TokenResponse {
    const tokens: TokenResponse = {
      ...this.authState().tokens,
      access_token: localStorage.getItem('access_token') ?? '',
      refresh_token: localStorage.getItem('refresh_token') ?? '',
      token_type: localStorage.getItem('token_type') || 'bearer'
    };
    this.authState.update(state => ({ ...state, tokens, isAuthenticated: true }));
    if (persist) {
      this.saveAuthState();
    }
    // Сессия снова есть — будущий провал рефреша снова должен выводить (см. saveTokens).
    this.isHandlingAuthFailure = false;
    return tokens;
  }

  private syncTokensFromOtherTab(event: StorageEvent): void {
    if (event.storageArea !== localStorage) return;
    if (event.key !== 'access_token' && event.key !== 'refresh_token') return;
    // BE-1518: у вкладки нет своей сессии (плашка) или в соседней вошли другим — решает сверка пары,
    // в том числе когда пару стёрли.
    if (!this.authState().tokens || this.otherUserSignedIn()) {
      this.reconcileStoredPair();
      return;
    }
    // Только во вкладке, где сессия есть, и только при полной паре. Удаление токенов
    // (выход в другой вкладке) здесь не разбираем: запрос без токена получит 401, и
    // вкладка выйдет сама.
    const storedAccess = localStorage.getItem('access_token');
    const current = this.authState().tokens;
    if (!current || !storedAccess || !localStorage.getItem('refresh_token')) {
      return;
    }
    // В другой вкладке вошли под ДРУГИМ пользователем. Запросы этой вкладки уже идут с
    // его токеном (интерсептор читает хранилище), а интерфейс — роль, права, профиль —
    // остался от прежнего. Перезагружаемся, чтобы вкладка целиком стала новым пользователем.
    if (this.isOtherUser(current.access_token, storedAccess)) {
      this.reloadForOtherUser();
      return;
    }
    this.adoptStoredTokens(false);
  }

  /**
   * Единый владелец тимдауна при провале рефреша: выход РОВНО один раз на всю
   * пачку параллельных 401. Вызывается ТОЛЬКО из tap.error общего пайпа — поэтому
   * выход не дублируется ни по ждущим запросам, ни с генерик-веткой auth.interceptor
   * (она не трогает 401 самих /auth/refresh и /auth/logout).
   */
  private handleRefreshFailure(): void {
    if (this.isHandlingAuthFailure) {
      return;
    }
    this.isHandlingAuthFailure = true;
    this.expireSession();
  }

  /**
   * BE-1478: сессия кончилась сама (refresh мёртв, токен отозван) — локальная очистка и
   * экран входа. POST /auth/logout здесь не шлём: отзывать по мёртвой паре нечего, а пара
   * в хранилище в этот момент может принадлежать другой вкладке — запрос отозвал бы её
   * живую сессию. Серверный выход — только по кнопке «Выйти» (logout()).
   */
  expireSession(): void {
    // BE-1491: пачка 401 — один конец сессии. Уход на вход уже идёт (вопрос «Уйти / Остаться»
    // ещё открыт) или на странице плашка «Сессия закончилась» — ничего не повторяем.
    if (this.sessionEnding || this.sessionEndedOnForm()) {
      return;
    }
    // BE-1518: по нему решаем, тот ли пользователь потом войдёт в соседней вкладке.
    if (this.authState().tokens) {
      this.endedSession = this.authState();
    }
    this.clearAuthData();
    // BE-1489: со страницы, открытой без входа (сброс пароля, регистрация, приглашение), не
    // уводим — сессия ей не нужна, а замена адреса стёрла бы ссылку из письма. Адрес берём
    // у браузера, а не у роутера: на старте приложения роутер ещё не знает текущей страницы.
    // Но если в полёте переход на закрытую страницу (вход уже ведёт в кабинет), уводим: иначе
    // она откроется без сессии.
    const pending = this.router.getCurrentNavigation();
    const pendingUrl = String(pending?.finalUrl ?? pending?.extractedUrl ?? '');
    if (!isPublicPath(this.location.path()) || !isPublicPath(pendingUrl)) {
      // BE-1491: идёт «Выход» — человек уже решил уйти, второй раз не спрашиваем.
      if (this.logoutInFlight) {
        void this.goToLogin(true);
        return;
      }
      // BE-1491: на экране уже открыт вопрос (человек уходил по ссылке или нажал «Выход») — он
      // устарел: закрываем его, и вопрос задаст уже конец сессии. Иначе второй вопрос отбился
      // бы сразу, и плашка появилась бы без «Остаться».
      if (this.confirmation.showModal()) {
        this.confirmation.onCancel();
      }
      this.sessionEnding = true;
      void Promise.resolve(this.goToLogin()).finally(() => (this.sessionEnding = false));
    }
  }

  /**
   * BE-1517: 401 не про протухший access — конец сессии, если 401 не устарел. Устарел — значит, пока
   * запрос летел, в соседней вкладке вышли и снова вошли: в хранилище уже не та пара, с которой он
   * ушёл (`sentAccessToken`; `null` — ушёл без токена). Тогда хранилище не трогаем:
   * - у вкладки нет своей сессии (её уже закончил ранний 401), идёт свой «Выход» или перезагрузка —
   *   ничего не делаем, ошибку получит вызывающий;
   * - вошёл другой пользователь — вкладку перезагружаем под ним;
   * - тот же (или владельца не определить) — берём его пару. `true` — повторить запрос с ней.
   */
  expireSessionUnlessStale(sentAccessToken: string | null): boolean {
    const storedAccess = localStorage.getItem('access_token');
    if (!storedAccess || !localStorage.getItem('refresh_token') || storedAccess === sentAccessToken) {
      this.expireSession();
      return false;
    }
    const own = this.authState().tokens;
    if (!own || this.logoutInFlight || this.otherUserSignedInState()) {
      return false;
    }
    if (this.isOtherUser(own.access_token, storedAccess)) {
      this.reloadForOtherUser();
      return false;
    }
    if (own.access_token !== storedAccess) {
      this.adoptStoredTokens(false);
    }
    return true;
  }

  /**
   * BE-1489: на вход — с заменой адреса. Иначе вход ложится новой записью поверх закрытой
   * страницы, и «Назад» возвращает на неё, а её гвард снова уводит на вход.
   * BE-1491: `withoutQuestion` — человек уже ответил на вопрос о несохранённых изменениях.
   */
  private goToLogin(withoutQuestion = false): Promise<boolean> {
    return this.router.navigate(
      ['/auth'],
      withoutQuestion ? { replaceUrl: true, info: LEAVE_WITHOUT_QUESTION } : { replaceUrl: true },
    );
  }

  getCurrentUser(): Observable<CurrentUser> {
    return this.apiService.getCurrentUser().pipe(
      tap((user) => {
        this.diagnostics.setCurrentUser(user);
        this.authState.update(state => ({
          ...state,
          currentUser: user
        }));
        this.saveAuthState();
      })
    );
  }

  /**
   * Выход. BE-1491: `withoutQuestion` — на вопрос о несохранённых изменениях уже ответили
   * «Уйти» (requestLogout), уход на вход его не повторяет.
   */
  logout(withoutQuestion = false): void {
    if (this.logoutInFlight) {
      return;
    }
    // BE-1490: вкладка уже перезагружается под вошедшим в соседней (перезагрузку могли
    // отменить из-за несохранённых изменений) — его сессию не отзываем, повторяем перезагрузку.
    if (this.otherUserSignedInState()) {
      this.reloadPage();
      return;
    }
    if (!localStorage.getItem('refresh_token') || !localStorage.getItem('access_token')) {
      this.clearAuthData();
      void this.goToLogin(withoutQuestion);
      return;
    }

    this.logoutInFlight = true;
    // BE-1490: выход идёт в ту же очередь, что и обновления пары всех вкладок, и пару читает уже
    // в ней: пока выход не решил, чью сессию чистить, обновление её не сменит. Своё обновление
    // этой вкладки он ещё и дожидается явно (его ошибка выходу не помеха): без navigator.locks
    // или по истечении ожидания очередь порядка не гарантирует.
    const ownRefresh$ = this.refreshInFlight$?.pipe(ignoreElements(), catchError(() => EMPTY)) ?? EMPTY;
    concat(ownRefresh$, this.refreshLock.run(() => this.logoutUnderLock()))
      .pipe(finalize(() => (this.logoutInFlight = false)))
      .subscribe((next) => {
        if (next === 'reload') {
          this.reloadForOtherUser();
        } else {
          void this.goToLogin(withoutQuestion);
        }
      });
  }

  /**
   * BE-1491: «Выход» по кнопке. На странице с несохранёнными изменениями сначала тот же вопрос,
   * что у гварда, и только после «Уйти» — выход; уход на вход этот вопрос уже не повторяет.
   * Страницу, не известную UnsavedChangesService, спросит, как раньше, сам гвард.
   */
  async requestLogout(): Promise<void> {
    // BE-1518: в соседней вкладке вошли под другим аккаунтом — выход у этой вкладки один: перезагрузка
    // под ним (про несохранённое спросит браузер). Его сессию не трогаем.
    if (this.otherUserSignedIn()) {
      this.reloadPage();
      return;
    }
    // При плашке сессии уже нет — серверный выход не нужен: в хранилище может лежать свежая
    // пара соседней вкладки (человек вошёл там, чтобы вставить данные), её не трогаем.
    if (this.sessionEndedOnForm()) {
      this.signInAgain();
      return;
    }
    // Выход уже идёт — второй раз не спрашиваем.
    if (this.logoutInFlight) {
      return;
    }
    const ask = this.unsavedChanges.hasAnyUnsavedChanges();
    if (ask && !(await this.confirmation.confirm(UNSAVED_CHANGES_CONFIRMATION))) {
      return;
    }
    this.logout(ask);
  }

  /**
   * BE-1518: можно ли запросу вкладки уйти с парой из хранилища; иначе — отказ вызывающему. Пару
   * сверяем на каждом запросе: в соседней вкладке могли войти раньше, чем дошло событие storage.
   */
  checkStoredPair(): SessionPairBlockedError | null {
    this.reconcileStoredPair();
    if (this.otherUserSignedInState()) {
      return new SessionPairBlockedError();
    }
    // Вопрос «Уйти / Остаться» при конце сессии ещё открыт: чья это пара, решим по ответу на него.
    if (!this.authState().tokens && this.sessionEnding && localStorage.getItem('access_token')) {
      return new SessionPairBlockedError('Сессия закончилась — войдите снова');
    }
    return null;
  }

  /**
   * BE-1518: сверка пары в хранилище с вкладкой — при показе плашки, на событие storage и на каждом
   * запросе. Вошёл владелец вкладки (или владельца не определить) — вкладка оживает; другой — его
   * пару не берём; пару стёрли — плашка «Сессия закончилась». Чужую пару узнаём уже по access:
   * заголовок интерсептор ставит по нему одному, refresh в хранилище может ещё не дописаться.
   */
  private reconcileStoredPair(): void {
    const storedAccess = localStorage.getItem('access_token');
    const pairComplete = !!storedAccess && !!localStorage.getItem('refresh_token');
    const own = this.authState().tokens;
    if (this.otherUserSignedInState()) {
      if (!storedAccess) {
        if (own) this.endedSession = this.authState();
        this.otherUserSignedInState.set(false);
        this.clearTabSession();
        this.stayWithoutSession(this.location.path());
      } else if (pairComplete && !this.storedPairOfOtherUser()) {
        this.revive();
      }
      return;
    }
    if (!own) {
      // Своей сессии нет: решаем на плашке; пока открыт вопрос — по ответу на него.
      if (this.sessionEndedOnForm() && storedAccess) {
        if (this.storedPairOfOtherUser()) {
          this.enterOtherUserState();
        } else if (pairComplete) {
          this.revive();
        }
      }
      return;
    }
    if (this.storedPairOfOtherUser()) {
      this.reloadForOtherUser();
    }
  }

  /** BE-1518: вкладка берёт пару своего владельца, вошедшего в соседней, и перечитывает профиль и права. */
  private revive(): void {
    // Своя сессия кончилась — возвращаем её прежнее состояние (роль, права): если /auth/me не
    // ответит, кабинет не должен счесть вкладку чужой и увести на вход.
    if (!this.authState().tokens && this.endedSession) {
      this.authState.set(this.endedSession);
    }
    this.endedSession = null;
    this.otherUserSignedInState.set(false);
    this.sessionEndedOnFormState.set(false);
    this.adoptStoredTokens(false);
    this.refreshPermissions();
  }

  /** BE-1518: вошёл другой — любой уход со страницы перезагружает вкладку уже под ним. */
  private enterOtherUserState(): void {
    if (this.otherUserSignedInState()) {
      return;
    }
    this.otherUserSignedInState.set(true);
    this.onLeavingPage(this.location.path(), () => {
      if (this.otherUserSignedInState()) this.reloadPage();
    });
  }

  /** BE-1518: пара в хранилище другого пользователя — владельцы известны у обоих и разные. */
  private storedPairOfOtherUser(): boolean {
    // Владелец вкладки — по её access, а после конца сессии — по запомненному состоянию.
    const owner = this.authState().tokens?.access_token ?? this.endedSession?.tokens?.access_token;
    return this.isOtherUser(owner, localStorage.getItem('access_token'));
  }

  /** BE-1491/1518: `then` — при первом завершённом переходе на другую страницу (не та же с другим query). */
  private onLeavingPage(url: string, then: () => void): void {
    const page = pathOnly(url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd && pathOnly(event.urlAfterRedirects) !== page),
        take(1),
      )
      .subscribe(then);
  }

  /** BE-1518: «Обновить страницу» на плашке «вошли под другим аккаунтом». */
  reloadTab(): void {
    this.reloadPage();
  }

  /** BE-1491: «Войти» на плашке «Сессия закончилась» — на вход без вопроса. */
  signInAgain(): void {
    void this.goToLogin(true);
  }

  /** BE-1491: идёт конец сессии — вопрос «Уйти / Остаться» гварда относится к нему. */
  get endingSession(): boolean {
    return this.sessionEnding;
  }

  /**
   * BE-1491: на вопрос при конце сессии ответили «Остаться» — на странице `url` плашка до
   * ухода с неё (первый завершённый переход на другую страницу) или до входа.
   */
  stayWithoutSession(url: string): void {
    if (this.sessionEndedOnForm()) {
      return;
    }
    this.sessionEndedOnFormState.set(true);
    this.onLeavingPage(url, () => this.sessionEndedOnFormState.set(false));
    // BE-1518: пока был открыт вопрос, в соседней вкладке могли уже войти.
    this.reconcileStoredPair();
  }

  /** BE-1490: сам выход — уже в очереди. */
  private logoutUnderLock(): Observable<AfterLogout> {
    const refreshToken = localStorage.getItem('refresh_token');
    const accessToken = localStorage.getItem('access_token');
    if (!refreshToken || !accessToken) {
      // Пока ждали очереди, сессию уже закрыли: вышли в соседней вкладке или она кончилась.
      this.clearAuthData();
      return of('login');
    }
    // Пока ждали, в соседней вкладке вошёл другой пользователь: в хранилище — его пара, а эта
    // вкладка уже перезагружается под ним (или вот-вот). Его сессию не отзываем.
    if (this.otherUserSignedInState() || this.isOtherUser(this.authState().tokens?.access_token, accessToken)) {
      return of('reload');
    }
    const sent: LogoutRequest = { refresh_token: refreshToken, access_token: accessToken };
    return this.apiService.logout(sent).pipe(
      // Успех или ошибка — локальную сессию закрываем одинаково.
      catchError(() => of(null)),
      map(() => this.closeSessionAfterLogout(sent)),
    );
  }

  /**
   * BE-1490: по ответу на выход — чью сессию чистить. Пока запрос летел, пару в хранилище мог
   * сменить вход в соседней вкладке (он идёт мимо очереди); решаем по владельцу пары:
   * - пары нет или она та же — очищаем;
   * - вошёл другой пользователь — его сессию не трогаем, вкладку перезагружаем под ним;
   * - тот же пользователь (или владельца не определить), но пара другая — очищаем и отзываем
   *   и её, иначе она останется живой на сервере. Повторный вход того же пользователя здесь
   *   неотличим от обновления пары — его сессия тоже закрывается (решение по тикету).
   */
  private closeSessionAfterLogout(sent: LogoutRequest): AfterLogout {
    const storedRefresh = localStorage.getItem('refresh_token');
    const storedAccess = localStorage.getItem('access_token');
    const replaced = !!storedRefresh && storedRefresh !== sent.refresh_token;
    if (replaced && this.isOtherUser(sent.access_token, storedAccess)) {
      return 'reload';
    }
    this.clearAuthData();
    if (replaced && storedRefresh) {
      this.revokeQuietly({ refresh_token: storedRefresh, access_token: storedAccess ?? '' });
    }
    return 'login';
  }

  /**
   * BE-1490: отозвать пару, которая больше никому не нужна, — в фоне: ответ ни на что не
   * влияет, и полноэкранное «нет связи» при обрыве не нужно.
   */
  private revokeQuietly(pair: LogoutRequest): void {
    this.apiService.logout(pair, withoutOfflineOverlay()).subscribe({ error: () => undefined });
  }

  /** BE-1490: токены разных пользователей — только если владелец известен у обоих. */
  private isOtherUser(ownAccess: string | null | undefined, storedAccess: string | null): boolean {
    const ownUserId = tokenUserId(ownAccess);
    const storedUserId = tokenUserId(storedAccess);
    return !!ownUserId && !!storedUserId && ownUserId !== storedUserId;
  }

  /** BE-1490: перезагрузка под пользователем, вошедшим в соседней вкладке, — один раз. */
  private reloadForOtherUser(): void {
    if (this.otherUserSignedInState()) return;
    // BE-1518: перезагрузку могут отменить (несохранённые данные) — тогда на странице плашка, а
    // запросы с его парой не уходят. Удалась — плашку никто не увидит.
    this.enterOtherUserState();
    this.reloadPage();
  }

  resetPassword(request: PasswordResetRequest): Observable<PasswordResetResponse> {
    const { lower, typed } = loginEmails(request.email);
    const reset$ = this.apiService.resetPassword({ ...request, email: lower });
    if (!typed) return reset$;
    // Бэк не говорит, нашёлся ли аккаунт, — письмо просим и на почту как её ввели (старый
    // аккаунт с заглавными). Ответ — от первого запроса, ошибка запасного не всплывает.
    return reset$.pipe(
      switchMap((response) =>
        this.apiService.resetPassword({ ...request, email: typed }).pipe(
          map(() => response),
          catchError(() => of(response))
        )
      )
    );
  }

  confirmPasswordReset(request: PasswordResetConfirmRequest): Observable<{ message: string }> {
    return this.apiService.confirmPasswordReset(request);
  }

  registerBusinessData(data: RegistrationData): Observable<any> {
    const payload: BusinessDataRequest = {
      ...data,
      company_type: ORGANIZATION_TYPE_TO_API_MAP[data.company_type]
    };

    return this.apiService.registerBusinessData(payload);
  }

  updateBusinessStatus(status: string): void {
    localStorage.setItem('business_status', status);
    this.authState.update(state => ({
      ...state,
      tokens: state.tokens ? {
        ...state.tokens,
        business_status: status
      } : null
    }));
    this.saveAuthState();
  }

  async getBusinessProfile(userId: number): Promise<BusinessProfile | null> {
    // В реальном приложении здесь был бы API запрос
    const stored = localStorage.getItem(`${this.PROFILE_KEY}-${userId}`);
    
    if (stored) {
      const profile = JSON.parse(stored);
      return {
        ...profile,
        createdAt: new Date(profile.createdAt),
        updatedAt: new Date(profile.updatedAt)
      };
    }

    return null;
  }

  async createBusinessProfile(profile: Omit<BusinessProfile, 'id' | 'createdAt' | 'updatedAt'>): Promise<BusinessProfile> {
    this.authState.update(state => ({ ...state, isLoading: true }));

    try {
      await this.simulateApiCall();

      const newProfile: BusinessProfile = {
        ...profile,
        id: this.generateId(),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Сохраняем профиль
      localStorage.setItem(`${this.PROFILE_KEY}-${profile.userId}`, JSON.stringify(newProfile));

      // Обновляем состояние
      this.authState.update(state => ({
        ...state,
        businessProfile: newProfile,
        isLoading: false
      }));

      this.saveAuthState();
      return newProfile;
    } catch (error) {
      this.authState.update(state => ({ ...state, isLoading: false }));
      throw error;
    }
  }

  async updateBusinessProfile(profile: BusinessProfile): Promise<BusinessProfile> {
    this.authState.update(state => ({ ...state, isLoading: true }));

    try {
      await this.simulateApiCall();

      const updatedProfile: BusinessProfile = {
        ...profile,
        updatedAt: new Date()
      };

      // Сохраняем профиль
      localStorage.setItem(`${this.PROFILE_KEY}-${profile.userId}`, JSON.stringify(updatedProfile));

      // Обновляем состояние
      this.authState.update(state => ({
        ...state,
        businessProfile: updatedProfile,
        isLoading: false
      }));

      this.saveAuthState();
      return updatedProfile;
    } catch (error) {
      this.authState.update(state => ({ ...state, isLoading: false }));
      throw error;
    }
  }


  private initializeAuth(): void {
    const stored = localStorage.getItem(this.AUTH_KEY);
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const tokenType = localStorage.getItem('token_type');
    const businessStatus = localStorage.getItem('business_status');
    const registrationStage = this.registrationStageFromStorage();

    if (stored) {
      const state = JSON.parse(stored);
      const tokens = accessToken && refreshToken ? {
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: tokenType || 'bearer',
        role: state.tokens?.role,
        business_status: businessStatus || undefined,
        registration_stage: registrationStage || undefined
      } : null;
      this.authState.set({
        ...state,
        tokens,
        createdAt: state.createdAt ? new Date(state.createdAt) : null,
        updatedAt: state.updatedAt ? new Date(state.updatedAt) : null
      });
      this.diagnostics.setCurrentUser(state.currentUser || null);
      this.diagnostics.setAuthContext(tokens?.role, tokens?.business_status, tokens?.registration_stage);
    } else if (accessToken && refreshToken) {
      // Если есть токены, но нет сохраненного состояния
      const tokens = {
        access_token: accessToken,
        refresh_token: refreshToken,
        token_type: tokenType || 'bearer',
        role: 'unknown', // Временное значение, будет обновлено после /auth/me
        business_status: businessStatus || undefined,
        registration_stage: registrationStage || undefined
      };
      this.authState.set({
        user: null,
        currentUser: null,
        businessProfile: null,
        isAuthenticated: true,
        isLoading: false,
        tokens
      });
      this.diagnostics.setAuthContext(tokens.role, tokens.business_status, tokens.registration_stage);
    }
  }

  private saveAuthState(): void {
    localStorage.setItem(this.AUTH_KEY, JSON.stringify(this.authState()));
  }

  saveTokens(tokens: TokenResponse): void {
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    localStorage.setItem('token_type', tokens.token_type);
    if (tokens.business_status) {
      localStorage.setItem('business_status', tokens.business_status);
    }
    if (tokens.registration_stage) {
      localStorage.setItem('registration_stage', tokens.registration_stage);
    }
    this.diagnostics.setAuthContext(tokens.role, tokens.business_status, tokens.registration_stage);
    // Свежая сессия сохранена → снимаем страж тимдауна: будущий провал рефреша
    // снова сможет разлогинить. (Страж дедупит пачку 401 ОДНОГО эпизода смерти
    // сессии; finalize пайпа refreshInFlight$ его НЕ сбрасывает — см. refreshToken().)
    this.isHandlingAuthFailure = false;
    // BE-1491: вошли — плашка «Сессия закончилась» больше не нужна.
    this.sessionEndedOnFormState.set(false);
    // BE-1518: вошли в этой вкладке — она больше не ждёт перезагрузки под другим.
    this.otherUserSignedInState.set(false);
    this.endedSession = null;
  }

  updateCurrentUser(user: CurrentUser): void {
    this.diagnostics.setCurrentUser(user);
    this.authState.update((state) => ({
      ...state,
      currentUser: user,
      isAuthenticated: true
    }));
    this.saveAuthState();
  }

  private clearAuthData(): void {
    localStorage.removeItem(this.AUTH_KEY);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('business_status');
    localStorage.removeItem('registration_stage');
    this.clearTabSession();
  }

  /** BE-1518: сессия этой вкладки — без общего хранилища: там может быть пара соседней. */
  private clearTabSession(): void {
    this.diagnostics.setCurrentUser(null);
    this.diagnostics.setAuthContext();
    
    this.authState.set({
      user: null,
      currentUser: null,
      businessProfile: null,
      isAuthenticated: false,
      isLoading: false,
      tokens: null
    });
  }

  private async simulateApiCall(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 1000));
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private registrationStageFromStorage(): TokenResponse['registration_stage'] {
    const value = localStorage.getItem('registration_stage');
    if (value === 'INITIAL_REGISTRATION' || value === 'DATA_PROVIDED' || value === 'ACCOUNT_CREATED') {
      return value;
    }
    return undefined;
  }
}
